import React, { useEffect, useState } from 'react'
import { cn } from 'utils/cn'
import { Dialog, DialogContent, DialogTitle, Button, Switch } from 'components/ui'

interface CookiePreferencesModalProps {
  isOpen: boolean
  onClose: () => void
  initialAnalytics: boolean
  onSave: (prefs: { analytics: boolean }) => void
}

const borderClass = 'border-gray-100 dark:border-gray-600'
const mutedClass = 'text-gray-500 dark:text-gray-400'

export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
  isOpen,
  onClose,
  initialAnalytics,
  onSave,
}) => {
  const [analytics, setAnalytics] = useState(initialAnalytics)

  useEffect(() => {
    if (isOpen) {
      setAnalytics(initialAnalytics)
    }
  }, [isOpen, initialAnalytics])

  const handleSave = () => {
    onSave({ analytics })
  }

  return (
    <Dialog open={isOpen} onOpenChange={o => !o && onClose()}>
      <DialogContent
        size="sm"
        hideClose
        className="overflow-hidden rounded-md p-0 shadow-[0_2px_16px_rgba(0,0,0,0.08)]"
      >
        <div className="px-5 pb-4 pt-5">
          <DialogTitle className={cn('text-content-secondary', 'text-[15px] font-semibold')}>
            Cookie settings
          </DialogTitle>
          <p
            className={cn('text-content-secondary', `mt-1 text-[13px] leading-snug ${mutedClass}`)}
          >
            Necessary cookies keep the site working. Analytics cookies help us improve.
          </p>
        </div>

        <div className={`border-t ${borderClass}`}>
          <div className={cn('flex flex-row items-center', 'justify-between px-5 py-3')}>
            <div>
              <p className={cn('text-content-secondary', 'text-[13px] font-medium')}>Necessary</p>
              <p className={cn('text-content-secondary', `text-[12px] ${mutedClass}`)}>Always on</p>
            </div>
            <Switch isChecked isDisabled />
          </div>

          <div
            className={cn(
              'flex flex-row items-center',
              `justify-between border-t px-5 py-3 ${borderClass}`,
            )}
          >
            <div>
              <p className={cn('text-content-secondary', 'text-[13px] font-medium')}>Analytics</p>
              <p className={cn('text-content-secondary', `text-[12px] ${mutedClass}`)}>
                Usage tracking
              </p>
            </div>
            <Switch isChecked={analytics} onChange={e => setAnalytics(e.target.checked)} />
          </div>
        </div>

        <div
          className={cn(
            'flex flex-row items-center',
            `justify-end gap-2 border-t px-5 py-3 ${borderClass}`,
          )}
        >
          <Button
            size="xs"
            variant="outline"
            className={`rounded-md px-4 ${borderClass}`}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="xs" colorScheme="accent" className="rounded-md px-4" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
